import { useState } from "react";
import { MoreVertical, Flag, Ban } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  { id: "SPAM", label: "Spam or scam" },
  { id: "FAKE", label: "Fake profile" },
  { id: "ABUSE", label: "Abuse or harassment" },
  { id: "OTHER", label: "Something else" },
];

export default function ProfileCardMenu({ targetUserId, targetName, onBlocked }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reason, setReason] = useState("SPAM");
  const [submitting, setSubmitting] = useState(false);

  const submitReport = async () => {
    setSubmitting(true);
    try {
      await api.post("/reports", { reported_user_id: targetUserId, reason });
      toast.success("Report submitted. Admin will review soon.");
      setReportOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to report");
    } finally {
      setSubmitting(false);
    }
  };

  const submitBlock = async () => {
    setSubmitting(true);
    try {
      await api.post("/blocks", { blocked_user_id: targetUserId });
      toast.success(`${targetName || "User"} blocked.`);
      setBlockOpen(false);
      onBlocked?.(targetUserId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to block");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`profile-menu-${targetUserId}`}
            aria-label="Profile options"
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded-full bg-white/80 hover:bg-slate-100 border border-slate-200 text-slate-600"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white border border-slate-200 w-44">
          <DropdownMenuItem
            data-testid={`menu-report-${targetUserId}`}
            onClick={() => setReportOpen(true)}
          >
            <Flag className="w-4 h-4 mr-2 text-amber-600" /> Report
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`menu-block-${targetUserId}`}
            onClick={() => setBlockOpen(true)}
            className="text-rose-600 focus:text-rose-700"
          >
            <Ban className="w-4 h-4 mr-2" /> Block user
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Report {targetName || "this profile"}</DialogTitle>
            <DialogDescription>
              Help us keep the community safe. An admin will review within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={reason} onValueChange={setReason} className="space-y-2 mt-1">
            {REASONS.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                <RadioGroupItem value={r.id} id={`reason-${r.id}`} data-testid={`report-reason-${r.id}`} />
                <Label htmlFor={`reason-${r.id}`} className="text-sm font-medium cursor-pointer flex-1">
                  {r.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportOpen(false)} className="text-slate-600">
              Cancel
            </Button>
            <Button
              data-testid="report-submit-btn"
              onClick={submitReport}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
            >
              {submitting ? "Submitting..." : "Submit report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block confirm dialog */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle>Block {targetName || "this user"}?</DialogTitle>
            <DialogDescription>
              They won't appear in your searches and neither of you will be able to send messages.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBlockOpen(false)} className="text-slate-600">
              Cancel
            </Button>
            <Button
              data-testid="block-confirm-btn"
              onClick={submitBlock}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
            >
              {submitting ? "Blocking..." : "Block user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
